export interface AudienceSelection {
  locations: string[];
  teams: string[];
  copilot_users_only: boolean;
  inclusions: string[];
  exclusions: string[];
}

export interface Reminder {
  reminder_number: number;
  /** Local wall-clock time in `reminder_timezone`, 'YYYY-MM-DDTHH:mm:00'. */
  send_datetime: string;
  /** DST-correct UTC instant of `send_datetime`, 'YYYY-MM-DDTHH:mm:ss'; set at send time. */
  send_datetime_utc?: string | null;
}

export interface CampaignForm {
  campaign_name: string;
  background: string;
  business_goal: string;
  communications_outcome: string;
  key_messages: string;
  tactics: string;
  audience_message: string;
  priority: 'Low' | 'Medium' | 'High' | string;
  channel: string;
  send_datetime: string | null;
  /** DST-correct UTC instant of the initial `send_datetime`; set at send time. */
  send_datetime_utc?: string | null;
  approver: string;
  audience: AudienceSelection;
  /** IANA zone the reminder times are expressed in; '' when no reminders. */
  reminder_timezone: string;
  /** That zone's UTC offset, e.g. 'GMT+05:30'; '' when no reminders. */
  reminder_timezone_offset: string;
  /** Empty when the user has not asked for reminders. */
  reminders: Reminder[];
}

export function defaultForm(): CampaignForm {
  return {
    campaign_name: '',
    background: '',
    business_goal: '',
    communications_outcome: '',
    key_messages: '',
    tactics: '',
    audience_message: '',
    priority: 'Medium',
    channel: '',
    send_datetime: null,
    send_datetime_utc: null,
    approver: '',
    audience: { locations: [], teams: [], copilot_users_only: true, inclusions: [], exclusions: [] },
    reminder_timezone: '',
    reminder_timezone_offset: '',
    reminders: [],
  };
}

export interface Person {
  id?: string;
  display_name: string;
  email: string;
  job_title?: string;
  department?: string;
  office_location?: string;
  photo_url?: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatResponse {
  conversation_id: string;
  thread_id: string;
  message_id: number;
  assistant_reply: string;
  updated_form: CampaignForm;
  form_patch: Partial<CampaignForm> & { audience?: Partial<AudienceSelection> };
}

export interface ConversationThreadResponse {
  thread_id: string;
}

export interface ConversationThreadSummary {
  thread_id: string;
  title: string;
  message_count: number;
  updated_at: string;
}

export interface ConversationTurn {
  conversation_id: string;
  message_id: number;
  user_message: string;
  assistant_reply: string;
  current_form: CampaignForm;
  form_patch: Partial<CampaignForm> & { audience?: Partial<AudienceSelection> };
  updated_form: CampaignForm;
  created_at: string;
}

export interface CampaignVersionRecord {
  campaign_id: string;
  campaign_alias: string;
  audience_count: number;
  version_number: number;
  conversation_id: string;
  status: string;
  requestor: string;
  submitted_form: CampaignForm;
  created_at: string;
}

export interface ConversationThreadDetail {
  thread_id: string;
  title: string;
  latest_message_id: number | null;
  current_form: CampaignForm;
  turns: ConversationTurn[];
  submitted_campaigns: CampaignVersionRecord[];
}

export interface ResolveAudienceResponse {
  total: number;
  people: Person[];
  already_included?: string[];
  not_in_audience_excluded?: string[];
}

export interface SendCampaignResponse {
  audience_count: number;
  detail: string;
  campaign_id: string;
  campaign_alias: string;
  version_number: number;
  conversation_id: string;
  thread_id: string;
  message_id: number;
}
